import { supabase } from './supabase';
import { sendEmail } from './email';

async function isLowestBid(requestId: string, newBidAmount: number): Promise<boolean> {
  const { data: previousBids, error } = await supabase
    .from('bids')
    .select('amount')
    .eq('request_id', requestId)
    .eq('status', 'active')
    .neq('amount', newBidAmount);

  if (error) throw error;

  // If there are no other bids, this is the lowest
  if (!previousBids || previousBids.length === 0) return true;

  // Check if this bid is lower than all others
  const lowestPreviousBid = Math.min(...previousBids.map(bid => bid.amount));
  return newBidAmount < lowestPreviousBid;
}

export async function sendBidNotification(requestId: string) {
  try {
    // Get the latest bid amount
    const { data: latestBid, error: bidError } = await supabase
      .from('bids')
      .select('amount')
      .eq('request_id', requestId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (bidError) throw bidError;

    // Only send notification if this is the lowest bid
    const isLowest = await isLowestBid(requestId, latestBid.amount);
    if (!isLowest) {
      console.log('Skipping notification - not the lowest bid');
      return { error: null };
    }

    // Get notification template
    const { data: template, error: templateError } = await supabase
      .from('notification_templates')
      .select('*')
      .eq('name', 'new_bid_notification')
      .single();

    if (templateError) throw templateError;

    // Get request details
    const { data: request, error: requestError } = await supabase
      .from('insurance_requests')
      .select('email, car_make, car_model')
      .eq('id', requestId)
      .single();

    if (requestError) throw requestError;

    // Customize email content
    const content = template.body
      .replace('{car_make}', request.car_make)
      .replace('{car_model}', request.car_model)
      .replace('{lowest_bid}', `£${latestBid.amount.toLocaleString()}`);

    // Send email
    await sendEmail(
      request.email,
      template.subject,
      content
    );

    return { error: null };
  } catch (err) {
    console.error('Failed to send bid notification:', err);
    return { error: err instanceof Error ? err : new Error('Failed to send notification') };
  }
}